import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Mail, Lock, User, KeyRound, ChevronDown, ShieldCheck, HeartHandshake, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { aviApi, googleLoginRedirect } from "@/lib/aviApi";
import { BrandMark } from "@/components/BrandMark";
import { cn } from "@/lib/utils";

export default function Welcome() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("signup");
  const [busy, setBusy] = useState(false);
  const [gviOpen, setGviOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", gvi_code: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (tab === "signup") {
        await aviApi.post("/auth/register", form);
        toast.success("Welcome! Avi is ready to meet you.");
      } else {
        await aviApi.post("/auth/login", { email: form.email, password: form.password });
        toast.success("Welcome back.");
      }
      navigate("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="consumer min-h-screen avi-warmth">
      <div className="max-w-6xl mx-auto px-5 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <BrandMark className="h-9 w-9" />
          <span className="font-display font-semibold text-[15px]">Persistent A.I.</span>
        </div>
        <Link to="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="enterprise-console-link">
          Enterprise console →
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-5 pt-6 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left: warm pitch */}
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08]">
            Meet <span className="text-primary">Avi</span> —<br />an AI that actually<br />remembers you.
          </h1>
          <p className="mt-4 text-muted-foreground text-[15px] leading-7 max-w-md">
            Your recipes, your plans, your work, your life — Avi keeps it all safely in one
            persistent memory and adapts to the way you think.
          </p>
          <div className="mt-8 space-y-3">
            {[
              { Icon: Brain, t: "Remembers everything you share", d: "and recalls it naturally, right when it matters." },
              { Icon: HeartHandshake, t: "Work & Personal, kept apart", d: "two profiles, one shared life story — never crossed." },
              { Icon: ShieldCheck, t: "Private by design", d: "secrets stay PIN-locked; sensitive details are redacted at the core." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 grid place-items-center shrink-0">
                  <Icon className="h-4.5 w-4.5 h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-medium">{t}</div>
                  <div className="text-xs text-muted-foreground">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: auth card */}
        <Card className="p-6 sm:p-8 bg-card border-border shadow-lg rounded-3xl" data-testid="auth-card">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="signup" data-testid="signup-tab">Create account</TabsTrigger>
              <TabsTrigger value="login" data-testid="login-tab">Log in</TabsTrigger>
            </TabsList>

            <TabsContent value="signup" className="mt-5 space-y-3">
              <div>
                <Label className="text-xs">Your name</Label>
                <div className="relative mt-1">
                  <User className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.name} onChange={set("name")} placeholder="Ava" className="pl-9" data-testid="signup-name-input" />
                </div>
              </div>
              <AuthFields form={form} set={set} idp="signup" />
              <Collapsible open={gviOpen} onOpenChange={setGviOpen}>
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="gvi-code-toggle">
                    <KeyRound className="h-3 w-3" /> Have a GVI code from your organization? (optional)
                    <ChevronDown className={cn("h-3 w-3 transition-transform", gviOpen && "rotate-180")} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <Input value={form.gvi_code} onChange={set("gvi_code")} placeholder="GVI governance code" data-testid="gvi-code-input" />
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Links your workspace knowledge to Avi's Work profile. You can also add it later in Settings.
                  </p>
                </CollapsibleContent>
              </Collapsible>
              <Button className="w-full h-11 rounded-xl" onClick={submit} disabled={busy} data-testid="signup-submit-button">
                <Sparkles className="h-4 w-4" /> {busy ? "Creating…" : "Start with Avi"}
              </Button>
            </TabsContent>

            <TabsContent value="login" className="mt-5 space-y-3">
              <AuthFields form={form} set={set} idp="login" />
              <Button className="w-full h-11 rounded-xl" onClick={submit} disabled={busy} data-testid="login-submit-button">
                {busy ? "Signing in…" : "Log in"}
              </Button>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[11px] text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" className="w-full h-11 rounded-xl" onClick={googleLoginRedirect} data-testid="google-login-button">
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>
          <p className="text-[11px] text-muted-foreground text-center mt-4">
            No GVI code needed — Avi is yours from the first hello.
          </p>
        </Card>
      </div>
    </div>
  );
}

function AuthFields({ form, set, idp }) {
  return (
    <>
      <div>
        <Label className="text-xs">Email</Label>
        <div className="relative mt-1">
          <Mail className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="pl-9" data-testid={`${idp}-email-input`} />
        </div>
      </div>
      <div>
        <Label className="text-xs">Password</Label>
        <div className="relative mt-1">
          <Lock className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input type="password" value={form.password} onChange={set("password")} placeholder="8+ characters" className="pl-9" data-testid={`${idp}-password-input`} />
        </div>
      </div>
    </>
  );
}
